import type { SupportedLocale } from "../../locales/catalog-registry";
import { buildHomeBrandCluster } from "./home-brand";

/**
 * Top application bar with title and optional trailing controls (locale, auth).
 *
 * @param options - Title text and optional trailing nodes.
 * @returns **`header`** with **`qz-app-header`** layout.
 */
export function createAppHeader(
  options: Readonly<{
    title: string;
    trailing?: ReadonlyArray<HTMLElement>;
    /**
     * When **`true`**, the leading title is the **`Q`** tile plus **`title`** as wordmark,
     * wrapped in an anchor to **`/`** for SPA home navigation.
     */
    homeBrand?: boolean;
  }>,
): HTMLElement {
  const header = document.createElement("header");
  header.className = "qz-app-header";

  const inner = document.createElement("div");
  inner.className = "qz-app-header__inner";

  const title =
    options.homeBrand === true
      ? document.createElement("a")
      : document.createElement("p");
  title.className = "qz-app-header__title";
  if (options.homeBrand === true) {
    const a = title as HTMLAnchorElement;
    a.href = "/";
    a.classList.add("home-brand");
    a.appendChild(buildHomeBrandCluster(options.title));
  } else {
    title.textContent = options.title;
  }
  inner.appendChild(title);

  const trail = document.createElement("div");
  trail.className = "qz-app-header__trail";
  if (options.trailing !== undefined) {
    for (const node of options.trailing) {
      trail.appendChild(node);
    }
  }
  inner.appendChild(trail);

  header.appendChild(inner);
  return header;
}

const ALL_LOCALES: SupportedLocale[] = ["en", "de", "fr", "da", "uk"];

/**
 * Select control to pick UI locale; caller persists choice (for example **`localStorage`** then reload).
 *
 * @param current - Active locale for this document.
 * @param label - Accessible name for the **`select`** (**`Translate`** output).
 * @param optionLabel - Maps each locale tag to visible option text (typically each language's autonym).
 * @param onChange - Invoked with the chosen locale when the value changes.
 * @returns Connected **`select`** element.
 */
export function createLocaleSwitcher(
  current: SupportedLocale,
  label: string,
  optionLabel: (loc: SupportedLocale) => string,
  onChange: (loc: SupportedLocale) => void,
): HTMLSelectElement {
  const sel = document.createElement("select");
  sel.className = "qz-locale-select";
  sel.setAttribute("aria-label", label);

  for (const loc of ALL_LOCALES) {
    const opt = document.createElement("option");
    opt.value = loc;
    opt.textContent = optionLabel(loc);
    sel.appendChild(opt);
  }
  sel.value = current;

  sel.addEventListener("change", () => {
    const next = sel.value as SupportedLocale;
    if (ALL_LOCALES.includes(next)) {
      onChange(next);
    }
  });

  return sel;
}
